import {
  combineLatestTyped,
  filterByLatest,
  filterValue,
  mapNullable,
  mergeTyped,
  tuple,
  useDataStream,
  useEventAsStream,
  useStream,
  useValueAsStream
} from 'annotation-tools/utils';
import { Observable } from 'rxjs';
import { map, mapTo } from 'rxjs/operators';
import { FetchErrorType } from '../../api/types/common/fetch-error-type';
import { FetchResultType } from '../../api/types/common/fetch-result';
import { IAPIValidationErrorType } from '../../api/types/common/validation-error';
import { viewTexts } from '../../constants/view-texts';

export type FormSelectStateConfigType<ErrorResponseBodyType> = {
  required: boolean;
  requiredCheckMessageTargetName: string;
  validationErrorContentMapFn: (
    content: IAPIValidationErrorType<ErrorResponseBodyType>
  ) => string[];
};

export const useFormSelectState = <
  SuccessResponseBodyType,
  ErrorResponseBodyType,
  T extends number | string
>(
  dialogIsOpen: boolean,
  fetchResult:
    | undefined
    | FetchResultType<
        SuccessResponseBodyType,
        FetchErrorType<IAPIValidationErrorType<ErrorResponseBodyType>>
      >,
  config: FormSelectStateConfigType<ErrorResponseBodyType>,
  initialValue: T | undefined = undefined
): [
  Observable<T | undefined>,
  Observable<string[]>,
  Observable<boolean>,
  (value: T | undefined) => void
] => {
  /* from props */

  const dialogIsOpen$ = useValueAsStream(dialogIsOpen);
  const fetchResult$ = useValueAsStream(fetchResult);
  const initialValue$ = useValueAsStream(initialValue);

  /* state */

  const [userSelect$, onSelect] = useEventAsStream<T | undefined>();

  /* values */

  const initSignal$ = useStream(
    dialogIsOpen$.pipe(filterValue(true)) // initialize on open dialog
  );

  const resetSignal$ = useStream(
    dialogIsOpen$.pipe(filterValue(false)) // reset on close dialog
  );

  const isModified$ = useDataStream<boolean>(
    false,
    mergeTyped(
      userSelect$.pipe(mapTo(true)), // 一度 userInput が変更されたら true に
      resetSignal$.pipe(mapTo(false)) // ダイアログを閉じたらリセット
    )
  );

  const value$ = useDataStream<T | undefined>(
    undefined,
    mergeTyped(
      userSelect$,
      combineLatestTyped(initialValue$, initSignal$).pipe(
        map(([v]) => v),
        filterByLatest(isModified$.pipe(map(b => !b)))
      ), // 一度編集を始めたら外部からの入力は無視
      resetSignal$.pipe(mapTo(undefined)) // reset on close dialog
    )
  );

  const errorsLocal$ = useDataStream<string[]>(
    [],
    value$.pipe(
      map(value =>
        config.required && value === undefined
          ? [
              viewTexts.form.requiredCheckMessage(
                config.requiredCheckMessageTargetName
              )
            ]
          : []
      )
    )
  );

  const errorsFromAPI$ = useDataStream<string[]>(
    [],
    fetchResult$.pipe(
      map(result =>
        mapNullable(
          result,
          res =>
            res.type === 'error' && res.body.type === 'validation-error'
              ? config.validationErrorContentMapFn(res.body.content)
              : [],
          []
        )
      )
    )
  );

  const errors$ = useDataStream<string[]>(
    [],
    combineLatestTyped(
      errorsLocal$,
      mergeTyped(
        errorsFromAPI$,
        value$.pipe(mapTo([])) // サーバーからのエラーは編集したら消す
      )
    ).pipe(
      map(([errorsLocal, errorsFromAPI]) => [...errorsLocal, ...errorsFromAPI])
    )
  );

  const formValueIsValid$ = useDataStream<boolean>(
    false,
    errors$.pipe(map(errors => errors.length === 0))
  );

  /**
   * @description
   * requiredな要素が空のとき，初期値でいきなりエラーは表示したくないがsubmitは禁止したいため，
   * 初期状態は《エラー表示はしないがフォームの値は不正》とする．
   */
  const errorsForView$ = useDataStream<string[]>(
    [],
    combineLatestTyped(isModified$, errors$).pipe(
      map(([isModified, errors]) => (isModified ? errors : []))
    )
  );

  return tuple(value$, errorsForView$, formValueIsValid$, onSelect);
};
